import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ChatEvidenceItem, ChatMessage } from '@sr-test-wizard/shared';
import { api } from '../lib/api';

interface Props {
  runId: string;
  testKey: string;
  initialMessages: ChatMessage[];
  /**
   * Optional callback exposed by Phase 6: when provided AND the tester has
   * sent at least one chat message, a "Re-analyze with chat" button is
   * rendered next to "Send" so the tester can promote their conversation
   * into authoritative guidance for a fresh AI verdict.
   */
  onReanalyzeWithChat?: () => void;
  reanalyzing?: boolean;
}

export function AIChat({
  runId,
  testKey,
  initialMessages,
  onReanalyzeWithChat,
  reanalyzing = false,
}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<ChatEvidenceItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  /**
   * Evidence items the user uploaded since the last `Send`. We pin their
   * filenames as a chip row on the next outgoing user message so the
   * transcript visibly reflects which file the chat AI was reasoning
   * about. After send, the list resets.
   */
  const [pendingAttachments, setPendingAttachments] = useState<ChatEvidenceItem[]>([]);
  const feedRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasUserMessage = messages.some((m) => m.role === 'user');

  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [messages.length]);

  // Load existing chat evidence once per (run, test) so previously
  // dropped files keep showing as chips after a refresh.
  useEffect(() => {
    api
      .get<ChatEvidenceItem[]>(`/api/runs/${runId}/tests/${testKey}/chat/evidence`)
      .then((items) => setEvidence(items ?? []))
      .catch(() => setEvidence([]));
  }, [runId, testKey]);

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      for (const f of list) fd.append('files', f);
      const res = await fetch(
        `/api/runs/${runId}/tests/${testKey}/chat/evidence`,
        { method: 'POST', body: fd },
      );
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        data?: ChatEvidenceItem[];
        error?: string;
      };
      if (!res.ok || !json.success || !json.data) {
        throw new Error(json.error || `Upload failed (${res.status})`);
      }
      setEvidence((prev) => [...prev, ...(json.data as ChatEvidenceItem[])]);
      setPendingAttachments((prev) => [...prev, ...(json.data as ChatEvidenceItem[])]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const removeEvidence = async (id: string) => {
    try {
      await api.delete(`/api/runs/${runId}/tests/${testKey}/chat/evidence/${id}`);
      setEvidence((prev) => prev.filter((e) => e.id !== id));
      setPendingAttachments((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const send = async () => {
    const question = draft.trim();
    if (!question) return;
    setBusy(true);
    setError(null);
    setDraft('');
    const optimistic: ChatMessage = {
      id: -Date.now(),
      role: 'user',
      content: question,
      ts: new Date().toISOString(),
    };
    const attachedForThisTurn = pendingAttachments;
    setMessages((prev) => [...prev, optimistic]);
    try {
      const data = await api.post<{ reply: string; message: ChatMessage }>(
        `/api/runs/${runId}/tests/${testKey}/chat`,
        { question },
      );
      setMessages((prev) => [...prev, data.message]);
      // Reset the pending chips only after a successful send; on
      // failure the user might want to retry with the same context.
      if (attachedForThisTurn.length > 0) setPendingAttachments([]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chat">
      <div className="row" style={{ margin: '0 0 8px', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Chat with AI about this test</h3>
        {evidence.length > 0 && (
          <span className="muted small">
            ({evidence.length} attached evidence file{evidence.length === 1 ? '' : 's'})
          </span>
        )}
      </div>
      <div
        ref={feedRef}
        className="messages"
        onDragOver={(e) => {
          if (e.dataTransfer?.types?.includes('Files')) {
            e.preventDefault();
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (e.dataTransfer?.files?.length) {
            e.preventDefault();
            setDragOver(false);
            void uploadFiles(e.dataTransfer.files);
          }
        }}
        style={
          dragOver
            ? {
                outline: '2px dashed var(--accent)',
                outlineOffset: -4,
                background: 'rgba(0, 122, 204, 0.06)',
              }
            : undefined
        }
      >
        {messages.length === 0 && (
          <p className="muted small">
            Ask a follow-up question or drop screenshots / files in to attach evidence. The AI
            sees the same source code, image analysis, existing assessment, and any files you
            attach below.
          </p>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`msg ${msg.role}`}>
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          </div>
        ))}
      </div>
      {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}

      {evidence.length > 0 && (
        <div
          className="row"
          style={{ flexWrap: 'wrap', gap: 4, margin: '4px 0' }}
          aria-label="Chat-attached evidence"
        >
          {evidence.map((item) => (
            <span
              key={item.id}
              className="badge small"
              title={`${item.mime} • ${(item.size / 1024).toFixed(1)} KB${
                item.analysis ? ' • read by AI' : ' • stored only'
              }`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <span>📎</span>
              <code className="mono small">{item.filename}</code>
              <button
                type="button"
                className="btn btn-ghost small"
                onClick={() => void removeEvidence(item.id)}
                title="Remove"
                style={{ padding: 0, lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="composer">
        <textarea
          value={draft}
          placeholder="Ask why the AI chose this verdict, what to verify in-game, or drag a file in to attach evidence."
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <div className="flex-col" style={{ gap: 4 }}>
          <button
            type="button"
            className="btn small"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            title="Attach screenshots or files to the chat"
          >
            {uploading ? 'Uploading...' : 'Attach'}
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || !draft.trim()}
            onClick={() => void send()}
          >
            {busy ? 'Sending...' : 'Send'}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) void uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      <div
        className="row"
        style={{ marginTop: 6, gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <p className="muted small" style={{ margin: 0 }}>
          Press Ctrl/Cmd + Enter to send. Drop files anywhere in the message area to attach.
        </p>
        {onReanalyzeWithChat && hasUserMessage && (
          <>
            <span className="spacer" />
            <button
              type="button"
              className="btn small"
              disabled={busy || reanalyzing}
              onClick={() => onReanalyzeWithChat()}
              title="Re-run the AI verdict for this test using this conversation as authoritative human guidance"
            >
              {reanalyzing ? 'Re-analyzing...' : 'Re-analyze with this chat'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
